import { Component, effect, inject, input, model, output, untracked } from '@angular/core';

import { AbstractControl, FormBuilder, ReactiveFormsModule, ValidationErrors, Validators, ValidatorFn } from '@angular/forms';
import { Dialog } from 'primeng/dialog';
import { DatePicker } from 'primeng/datepicker';
import { Textarea } from 'primeng/textarea';
import { Button } from 'primeng/button';
import { Pessoa } from '@/shared/models/pessoa-info.model';
import { DateUtils } from '@/shared/utils/date-utils';
import { FormularioValidatorUtil } from '@/shared/utils/formulario-validator-util';

import { DialogoPadraoDirective } from '@/shared/components/layout/dialogo-padrao/dialogo-padrao.directive';
@Component({
    selector: 'app-anotacao-nova',
    standalone: true,
    imports: [DialogoPadraoDirective, ReactiveFormsModule, Dialog, DatePicker, Textarea, Button],
    templateUrl: './nova-anotacao.html',
    styleUrl: './nova-anotacao.scss'
})
export class NovaAnotacaoDialog {
    // Controle externo do diálogo, seguindo o padrão do projeto
    showDialog = model.required<boolean>();
    pessoa = input<Pessoa>();
    /** Gravação em andamento no pai: trava o botão para não duplicar a anotação. */
    salvando = input(false);

    // Emite o payload básico para o componente pai tratar a inclusão
    anotacaoCriada = output<{ dtHistoricoMedico: string; txHistoricoMedico: string }>();

    private readonly fb = inject(FormBuilder);
    readonly form = this.fb.group({
        data: this.fb.control<Date | null>(null, [Validators.required, this.dataNaoFuturaValidator()]),
        anotacoes: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(250)]]
    });

    constructor() {
        // Quem fecha o diálogo é o pai, no sucesso — então a limpeza precisa acompanhar o
        // fechamento, não o clique em Salvar. Sem isto, o texto sobreviveria para a próxima
        // abertura.
        effect(() => {
            if (!this.showDialog()) {
                untracked(() => this.form.reset());
            }
        });
    }

    hideDialog(): void {
        this.showDialog.set(false);
    }

    /** Impede registrar uma anotação com data posterior à data atual. */
    private dataNaoFuturaValidator(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const valor = control.value as Date | null;
            if (!valor) return null;
            return FormularioValidatorUtil.dataMaiorQueHoje(DateUtils.toIsoDate(valor)) ? { dataFutura: true } : null;
        };
    }

    /** Mensagem de erro do campo de data: obrigatoriedade tem prioridade sobre a regra de não-futura. */
    protected mensagemErroData(): string {
        const campo = this.form.get('data');
        if (!campo?.errors) return '';
        if (campo.errors['required']) return 'Campo obrigatório.';
        if (campo.errors['dataFutura']) return 'A data da anotação não pode ser superior à data atual.';
        return 'Valor inválido.';
    }

    salvar(): void {
        if (this.form.invalid) {
            this.form.markAllAsTouched();
            return;
        }
        const { data, anotacoes } = this.form.value as { data: Date | string; anotacoes: string };
        const iso = this.toIsoDateString(data);
        // Não fecha aqui: quem sabe se a gravação deu certo é o pai. Fechar no clique descartava o
        // texto digitado quando a API recusava, deixando o usuário só com o toast de erro e nada
        // para corrigir.
        this.anotacaoCriada.emit({ dtHistoricoMedico: iso, txHistoricoMedico: anotacoes });
    }

    private toIsoDateString(value: Date | string | null | undefined): string {
        if (!value) return '';
        try {
            const d = typeof value === 'string' ? new Date(value) : value;
            const yyyy = d.getFullYear();
            const mm = String(d.getMonth() + 1).padStart(2, '0');
            const dd = String(d.getDate()).padStart(2, '0');
            return `${yyyy}-${mm}-${dd}`;
        } catch {
            return String(value);
        }
    }
}
